/**
 * The guest sign-up gate (SCRUM-274) — opened by the limit, a locked feature
 * (attachments) or the locked history; never before the limit (contract: no
 * prompt before it is reached).
 *
 * DRAFTED STATE (GUARDRAILS rule 10): the prototype has no sign-up gate. It is
 * composed from `@garden/ui` BottomSheet (dialog semantics, focus trap,
 * Escape, focus restore, reduced-motion slide — precedent NeedSeeAllSheet) and
 * DESIGN.md's button ladder: Google as the outlined row (its mark keeps its
 * own colours — the one sanctioned exception, see OAuthButtons), «Создать
 * аккаунт» as the primary fill, «Войти» as the secondary wash. Every choice
 * carries `return_to=/chat?t=<thread>` and records the claim intent first, so
 * whichever account the visitor ends up in claims this conversation without
 * asking (W2.6).
 */
import { BottomSheet } from "@garden/ui";
import { useEffect, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { pressClass } from "@/components/motion";
import { GoogleIcon, buildOAuthHref } from "@/features/auth/components/OAuthButtons";
import { safeReturnToOrNull, withReturnTo } from "@/features/auth/destination";
import type { GuestAllowance, GuestGate } from "@/features/chat/guest/allowance";
import { recordClaimIntent } from "@/features/chat/guest/pointer";
import { AUTH_PATHS } from "@/lib/api-client";

export interface GateCopyKeys {
  title: string;
  /** Plural count for the title (the message limit). */
  titleCount?: number;
  body: string;
}

/** The i18n keys for a gate (pure — unit-testable, shared by sheet and card). */
export function gateCopyKeys(gate: GuestGate, allowance: GuestAllowance | null): GateCopyKeys {
  if (gate.reason === "attachments") {
    return { title: "guest.gate.titleAttachments", body: "guest.gate.bodyAttachments" };
  }
  if (gate.reason === "history") {
    return { title: "guest.gate.titleHistory", body: "guest.gate.bodyHistory" };
  }
  if (gate.reason === "voice") {
    return { title: "guest.gate.titleVoice", body: "guest.gate.bodyVoice" };
  }
  switch (gate.cause) {
    case "messages":
      return allowance !== null
        ? {
            title: "guest.gate.titleLimit",
            titleCount: allowance.max_messages,
            body: gate.hard ? "guest.gate.bodyLimit" : "guest.gate.bodySoft",
          }
        : {
            title: "guest.gate.titleTokens",
            body: gate.hard ? "guest.gate.bodyLimit" : "guest.gate.bodySoft",
          };
    case "tokens":
      return { title: "guest.gate.titleTokens", body: "guest.gate.bodyLimit" };
    case "budget":
      return { title: "guest.gate.titlePaused", body: "guest.gate.bodyPaused" };
    case "disabled":
      return { title: "guest.gate.titleDisabled", body: "guest.gate.bodyDisabled" };
  }
}

export function useGateCopy(
  gate: GuestGate | null,
  allowance: GuestAllowance | null,
): { title: string; body: string } | null {
  const { t } = useTranslation("chat");
  if (gate === null) return null;
  const keys = gateCopyKeys(gate, allowance);
  return {
    title:
      keys.titleCount !== undefined ? t(keys.title, { count: keys.titleCount }) : t(keys.title),
    body: t(keys.body),
  };
}

const ACTION_BASE = [
  "inline-flex min-h-12 items-center justify-center gap-2.5 rounded-[var(--g-radius)] px-[18px]",
  "text-[15.5px] font-semibold transition-[background-color,filter] duration-150",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
  "focus-visible:ring-offset-2",
  "motion-reduce:transition-none",
  pressClass("cta"),
].join(" ");
/**
 * Fix round 1: the ring-OFFSET colour must match whichever surface these
 * actions currently sit on, or the ring reads as a faint halo instead of a
 * clean inset. `GuestGateSheet` renders them inside BottomSheet's panel
 * (`--g-elevated`, `layout="stack"`); `GuestGateCard` renders them on its
 * own card surface (`--g-surface`, `layout="row"`).
 */
function actionClass(variant: string, layout: "stack" | "row"): string {
  const ringOffset =
    layout === "row"
      ? "focus-visible:ring-offset-[var(--g-surface)]"
      : "focus-visible:ring-offset-[var(--g-elevated)]";
  return `${ACTION_BASE} ${ringOffset} ${variant}`;
}
const PRIMARY_VARIANT = "bg-primary text-[var(--g-on-primary)] hover:brightness-110";
const SECONDARY_VARIANT = "bg-[var(--g-primary-soft)] text-[var(--g-primary-ink)] hover:brightness-95";
const OUTLINE_VARIANT = "border border-[var(--g-rule)] bg-[var(--g-surface)] text-foreground hover:bg-[var(--g-sunken)]";
const GHOST_CLASS = [
  "inline-flex min-h-11 items-center justify-center rounded-[var(--g-radius)] px-4",
  "text-[15px] font-semibold text-primary transition-colors duration-150",
  "hover:bg-[var(--g-primary-soft)] focus-visible:outline-none focus-visible:ring-2",
  "focus-visible:ring-[var(--g-primary)] motion-reduce:transition-none",
].join(" ");

export interface GuestSignInActionsProps {
  /** Always `/chat?t=<thread>` or `/chat` (`guestReturnTo`). */
  returnTo: string;
  layout: "stack" | "row";
}

/** Continue with Google · Create account · Log in (spec order). */
export function GuestSignInActions({ returnTo, layout }: GuestSignInActionsProps): ReactElement {
  const { t } = useTranslation("chat");
  // Fix round 1 (plan-mandated): every outgoing link goes through the app's
  // ONE return_to sanitiser (`features/auth/destination.ts`) instead of a
  // local `URLSearchParams` build — byte-identical for the safe values
  // `guestReturnTo` actually produces (`/chat` or `/chat?t=<uuid>`), and a
  // hostile value (`//evil.example`, `https://evil.example`) falls back to
  // `/chat` uniformly across all three rather than silently dropping the
  // param on two of them and keeping it on the third. The guardrail must not
  // depend on the caller's discipline alone (same reasoning as pointer.ts's
  // own hardening pass).
  const safeReturn = safeReturnToOrNull(returnTo) ?? "/chat";
  const googleHref = buildOAuthHref(AUTH_PATHS.oauthGoogleStart, safeReturn);
  const signupTo = withReturnTo("/signup", safeReturn);
  const loginTo = withReturnTo("/login", safeReturn);
  // Synchronous, before the browser follows the link (incl. the Google
  // full-page navigation) — on a plain click AND on aux-click. Fix round:
  // cmd/ctrl+click (open in a new tab) still fires an ordinary `click`, so
  // `onClick` alone already covers it; `auxclick` is the one gesture it
  // misses — the MIDDLE mouse button, a completely ordinary way to open
  // any of these three in a new tab.
  const record = (): void => {
    recordClaimIntent();
  };
  return (
    <div
      className={layout === "row" ? "flex flex-wrap gap-2" : "flex flex-col gap-2"}
      data-testid="chat-guest-signin-actions"
    >
      {/* A real <a href>: the OAuth state cookie is the CSRF defence, so the
          start must be a genuine same-origin navigation (see OAuthButtons). */}
      <a
        href={googleHref}
        onClick={record}
        onAuxClick={record}
        className={actionClass(OUTLINE_VARIANT, layout)}
        data-testid="chat-guest-google"
      >
        <span className="grid size-5 shrink-0 place-items-center">
          <GoogleIcon />
        </span>
        {t("guest.gate.google")}
      </a>
      <Link
        to={signupTo}
        onClick={record}
        onAuxClick={record}
        className={actionClass(PRIMARY_VARIANT, layout)}
        data-testid="chat-guest-signup"
      >
        {t("guest.gate.signup")}
      </Link>
      <Link
        to={loginTo}
        onClick={record}
        onAuxClick={record}
        className={actionClass(SECONDARY_VARIANT, layout)}
        data-testid="chat-guest-login"
      >
        {t("guest.gate.login")}
      </Link>
    </div>
  );
}

export interface GuestGateSheetProps {
  /** Why it is open; null = closed. */
  gate: GuestGate | null;
  allowance: GuestAllowance | null;
  returnTo: string;
  onClose: () => void;
  /** Attachments gate: close and put the paste lead-in in the composer. */
  onPasteInstead: () => void;
  /** Right-side dialog from 768px (BottomSheet's desktop variant). */
  desktop: boolean;
}

export function GuestGateSheet({
  gate,
  allowance,
  returnTo,
  onClose,
  onPasteInstead,
  desktop,
}: GuestGateSheetProps): ReactElement {
  const { t } = useTranslation("chat");
  const copy = useGateCopy(gate, allowance);
  const soft = gate !== null && gate.reason === "limit" && !gate.hard;
  const isOpen = gate !== null;

  // Consume Escape (preventDefault) before it can bubble to a sheet stacked
  // UNDERNEATH this one — e.g. GuestHistoryPanel's mobile sheet, which opens
  // first (from its own "sign in" CTA) and whose Escape handler was fixed
  // (W2.4b fix round 1) to skip closing when `event.defaultPrevented` is
  // already true. Registered on the CAPTURE phase, which always runs ahead
  // of any bubble-phase `document` listener for a real (non-synthetic) key
  // event — regardless of attachment order, i.e. regardless of which sheet
  // opened first. BottomSheet's own Escape handler only calls
  // `stopPropagation()`, not `preventDefault()` (checked — see
  // BottomSheet.tsx), so this sheet needs its own in addition.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDownCapture = (event: KeyboardEvent): void => {
      if (event.key === "Escape") event.preventDefault();
    };
    document.addEventListener("keydown", onKeyDownCapture, { capture: true });
    return () => document.removeEventListener("keydown", onKeyDownCapture, { capture: true });
  }, [isOpen]);

  return (
    <BottomSheet open={gate !== null} onClose={onClose} title={copy?.title ?? ""} desktop={desktop}>
      {gate !== null && copy !== null ? (
        <div className="flex flex-col gap-4" data-testid="chat-guest-gate" data-reason={gate.reason}>
          <p className="text-[15.5px] leading-[1.55] text-[var(--g-ink-2)]">{copy.body}</p>
          <GuestSignInActions returnTo={returnTo} layout="stack" />
          {gate.reason === "attachments" ? (
            <button
              type="button"
              onClick={onPasteInstead}
              className={GHOST_CLASS}
              data-testid="chat-guest-paste-instead"
            >
              {t("guest.gate.pasteInstead")}
            </button>
          ) : null}
          {soft ? (
            <button type="button" onClick={onClose} className={GHOST_CLASS} data-testid="chat-guest-continue">
              {t("guest.gate.continueGuest")}
            </button>
          ) : null}
        </div>
      ) : null}
    </BottomSheet>
  );
}

export default GuestGateSheet;
